import { useCallback, useMemo } from "react";
import {
  InfiniteData,
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  getChatInboxService,
  getChatMessagesService,
  getChatThreadsService,
  postChatMessageService,
  postChatReadService,
} from "@/services/chat.service";
import type {
  ChatInboxResponse,
  ChatMessage,
  ChatMessagesPage,
  ChatSocketStatus,
  ChatThreadsResponse,
} from "@/types/chat.types";

export const chatQueryKeys = {
  all: () => ["chat"] as const,
  deal: (dealId: string) => [...chatQueryKeys.all(), dealId] as const,
  threads: (dealId: string) => [...chatQueryKeys.deal(dealId), "threads"] as const,
  messages: (dealId: string, threadId: string) => [...chatQueryKeys.deal(dealId), "messages", threadId] as const,
  /** Every deal the viewer was invited into, with its chat summary. */
  inbox: () => [...chatQueryKeys.all(), "inbox"] as const,
  // Deliberately outside the `chat` prefix: a reconnect invalidates everything
  // under `chat`, and the outbox (unsent + failed bubbles) must survive that.
  outbox: (dealId: string, threadId: string) => ["chatOutbox", dealId, threadId] as const,
};

/** While the socket is down, fall back to polling at this interval. */
const POLL_WHILE_OFFLINE_MS = 15_000;

type MessagesData = InfiniteData<ChatMessagesPage, string | undefined>;

// ============ Cache helpers ============
function byCreatedAt(a: ChatMessage, b: ChatMessage) {
  return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0;
}

/** Insert or replace by id, dropping any optimistic twin with the same client_id. */
function upsertMessage(list: ChatMessage[], message: ChatMessage): ChatMessage[] {
  const rest = list.filter((m) => m.id !== message.id
    && !(message.client_id && m.client_id === message.client_id && m.sender_id === message.sender_id));
  return [...rest, message].sort(byCreatedAt);
}

export function applyMessage(queryClient: QueryClient, dealId: string, message: ChatMessage, viewerId?: string) {
  queryClient.setQueryData<MessagesData>(chatQueryKeys.messages(dealId, message.thread_id), (data) => {
    if (!data?.pages.length) return data;
    const [newest, ...older] = data.pages;
    if (older.some((page) => page.messages.some((m) => m.id === message.id))) return data;
    return { ...data, pages: [{ ...newest, messages: upsertMessage(newest.messages, message) }, ...older] };
  });

  let known = true;
  queryClient.setQueryData<ChatThreadsResponse>(chatQueryKeys.threads(dealId), (data) => {
    if (!data) return data;
    const thread = data.threads.find((t) => t.id === message.thread_id);
    if (!thread) {
      known = false;
      return data;
    }
    const current = thread.last_message;
    if (current?.id === message.id) return data;
    const isNewer = !current || current.created_at <= message.created_at;
    const fromOther = message.sender_id !== viewerId;
    const unreadAfterRead = !thread.last_read_at || message.created_at > thread.last_read_at;
    const threads = data.threads.map((t) => (t.id === message.thread_id
      ? {
        ...t,
        last_message: isNewer ? message : current,
        unread_count: fromOther && unreadAfterRead ? t.unread_count + 1 : t.unread_count,
      }
      : t));
    threads.sort((a, b) => (b.last_message?.created_at || "").localeCompare(a.last_message?.created_at || ""));
    return { ...data, threads };
  });
  // A thread this page has never listed — someone just accepted their invite.
  if (!known) queryClient.invalidateQueries({ queryKey: chatQueryKeys.threads(dealId) });

  let inInbox = true;
  queryClient.setQueryData<ChatInboxResponse>(chatQueryKeys.inbox(), (data) => {
    if (!data) return data;
    const row = data.deals.find((d) => d.deal_id === dealId);
    if (!row) {
      inInbox = false;
      return data;
    }
    if (row.last_message?.id === message.id) return data;
    const bump = message.sender_id !== viewerId ? 1 : 0;
    const deals = data.deals.map((d) => (d.deal_id === dealId
      ? {
        ...d,
        unread_count: d.unread_count + bump,
        last_message: !d.last_message || d.last_message.created_at <= message.created_at ? message : d.last_message,
      }
      : d));
    deals.sort((a, b) => (b.last_message?.created_at || "").localeCompare(a.last_message?.created_at || ""));
    return { ...data, deals, total_unread: data.total_unread + bump };
  });
  if (!inInbox) queryClient.invalidateQueries({ queryKey: chatQueryKeys.inbox() });
}

export function applyRead(queryClient: QueryClient, dealId: string, threadId: string, lastReadAt: string) {
  queryClient.setQueryData<ChatThreadsResponse>(chatQueryKeys.threads(dealId), (data) => data && ({
    ...data,
    threads: data.threads.map((t) => (t.id === threadId ? { ...t, unread_count: 0, last_read_at: lastReadAt } : t)),
  }));
  // Per-thread counts aren't in the inbox, so re-read the (small) summary.
  queryClient.invalidateQueries({ queryKey: chatQueryKeys.inbox() });
}

// ============ Queries ============
export const useChatInbox = (socketStatus: ChatSocketStatus, enabled = true) => {
  return useQuery<ChatInboxResponse, Error>({
    queryKey: chatQueryKeys.inbox(),
    queryFn: getChatInboxService,
    enabled,
    refetchInterval: socketStatus === "open" ? false : POLL_WHILE_OFFLINE_MS,
  });
};

export const useChatThreads = (dealId: string | undefined, socketStatus: ChatSocketStatus) => {
  return useQuery<ChatThreadsResponse, Error>({
    queryKey: chatQueryKeys.threads(dealId || ""),
    queryFn: () => getChatThreadsService(dealId as string),
    enabled: Boolean(dealId),
    refetchInterval: socketStatus === "open" ? false : POLL_WHILE_OFFLINE_MS,
  });
};

export const useChatMessages = (dealId: string, threadId: string | null, socketStatus: ChatSocketStatus) => {
  return useInfiniteQuery<ChatMessagesPage, Error, MessagesData, ReturnType<typeof chatQueryKeys.messages>, string | undefined>({
    queryKey: chatQueryKeys.messages(dealId, threadId || ""),
    queryFn: ({ pageParam }) => getChatMessagesService(dealId, threadId as string, pageParam),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => (lastPage.has_more ? lastPage.messages[0]?.created_at : undefined),
    enabled: Boolean(dealId && threadId),
    refetchInterval: socketStatus === "open" ? false : POLL_WHILE_OFFLINE_MS,
  });
};

/** Oldest first, across every loaded page. */
export function flattenMessages(data: MessagesData | undefined): ChatMessage[] {
  if (!data) return [];
  return [...data.pages].reverse().flatMap((page) => page.messages);
}

export const useMarkChatRead = (dealId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => postChatReadService(dealId, threadId),
    onSuccess: (res, threadId) => {
      if (res.data?.last_read_at) applyRead(queryClient, dealId, threadId, res.data.last_read_at);
    },
  });
};

// ============ Sending ============
function newClientId(): string {
  // randomUUID needs a secure context; plain-http LAN previews don't have one.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

interface Sender {
  id: string;
  name: string;
  role: string;
  internal: boolean;
}

/**
 * Optimistic send with an outbox. The bubble appears at once as "sending";
 * success swaps it for the stored row (the socket echo may beat the response —
 * both are keyed on client_id so the thread never shows it twice); failure
 * leaves it in place marked "not sent" with a retry that reuses the same
 * client_id, which the server treats as idempotent.
 */
export function useSendChatMessage(dealId: string, threadId: string | null, sender: Sender | null) {
  const queryClient = useQueryClient();
  const outboxKey = chatQueryKeys.outbox(dealId, threadId || "");
  const { data: outbox = [] } = useQuery<ChatMessage[]>({
    queryKey: outboxKey,
    queryFn: () => [],
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: Boolean(threadId),
  });

  const setOutbox = useCallback((update: (items: ChatMessage[]) => ChatMessage[]) => {
    queryClient.setQueryData<ChatMessage[]>(outboxKey, (items) => update(items || []));
  }, [queryClient, outboxKey]);

  const deliver = useCallback((draft: ChatMessage) => {
    if (!threadId) return;
    postChatMessageService(dealId, threadId, { body: draft.body, client_id: draft.client_id as string })
      .then((message) => {
        applyMessage(queryClient, dealId, message, sender?.id);
        setOutbox((items) => items.filter((m) => m.client_id !== draft.client_id));
      })
      .catch(() => {
        setOutbox((items) => items.map((m) => (m.client_id === draft.client_id ? { ...m, pending: false, failed: true } : m)));
      });
  }, [dealId, threadId, queryClient, sender?.id, setOutbox]);

  const send = useCallback((body: string) => {
    const text = body.trim();
    if (!text || !threadId || !sender) return;
    const clientId = newClientId();
    const draft: ChatMessage = {
      id: `pending-${clientId}`,
      deal_id: dealId,
      thread_id: threadId,
      sender_id: sender.id,
      sender_name: sender.name,
      sender_role: sender.role,
      sender_internal: sender.internal,
      body: text,
      client_id: clientId,
      created_at: new Date().toISOString(),
      pending: true,
    };
    setOutbox((items) => [...items, draft]);
    deliver(draft);
  }, [dealId, threadId, sender, setOutbox, deliver]);

  const retry = useCallback((message: ChatMessage) => {
    setOutbox((items) => items.map((m) => (m.client_id === message.client_id ? { ...m, pending: true, failed: false } : m)));
    deliver(message);
  }, [setOutbox, deliver]);

  const discard = useCallback((message: ChatMessage) => {
    setOutbox((items) => items.filter((m) => m.client_id !== message.client_id));
  }, [setOutbox]);

  return useMemo(() => ({ outbox, send, retry, discard }), [outbox, send, retry, discard]);
}

/** Total unread across every conversation the viewer can see — the tab badge. */
export function totalUnread(data: ChatThreadsResponse | undefined): number {
  return (data?.threads || []).reduce((sum, t) => sum + (t.unread_count || 0), 0);
}
